// Implementation of the Role entity relation addon (depends on BusinessArea)
import React from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { EntityRelationMetadata } from "../EntityRelationsManager";
import { BUSINESS_AREA_ENTITY_TYPE } from "../BusinessAreaAddon/BusinessAreaAddon";
import { AddonParams } from "../../base/BaseAddon";
import { StepChangeEvent, StepType, ValidationError, ValidationResult } from "../../../types/types";
import toastService from "../../../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../../core/toaster/Toaster_model";
import { RoleValidationView } from "./RoleValidation_view";
import { observable } from "mobx";
import { EntityRelationAddon } from "../EntityRelationAddon";
import OrganisationsApi, {
  OrganisationsApi as IOrganisationsApi
} from "../../../../../../services/api/v2/organisations/Organisations.api";
import RolesApi, { RolesApi as IRolesApi } from "../../../../../../services/api/v2/roles/Roles.api";
import { CommonColDefFieldNamesEnum } from "../../../../../../core/grids/builder/enums/AgGridColDefFieldNameEnum";

export const ROLE_ENTITY_TYPE = "role";

export const roleMetadata: EntityRelationMetadata = {
  entityType: ROLE_ENTITY_TYPE,
  displayName: "Roles",
  dependencies: [BUSINESS_AREA_ENTITY_TYPE],
  requiredForEntities: ["stakeholder"]
};

export class RoleAddon extends EntityRelationAddon {
  @observable data: FP.Entities.IRole[];
  organisationProvider: IOrganisationsApi;
  roleProvider: IRolesApi;
  organisationId: number;
  conflictFields?: string[];

  constructor(onNext: (event: StepChangeEvent) => void, onBack: () => void, params: AddonParams) {
    super("RoleAddon", ROLE_ENTITY_TYPE, roleMetadata, onNext, onBack, params);
    this.organisationProvider = OrganisationsApi;
    this.roleProvider = RolesApi;
    this.organisationId = params.organisationId;
    this.conflictFields = [CommonColDefFieldNamesEnum.Name];
  }

  async getExistingData(): Promise<FP.Entities.IRole[]> {
    try {
      const res = await this.roleProvider.getByOrganisationId(this.organisationId);
      return res.payload || [];
    } catch (error) {
      console.error("Error fetching existing roles:", error);
      return [];
    }
  }

  // temp
  async getExistingEntities(): Promise<FP.Entities.IRole[]> {
    return this.getExistingData();
  }

  async getExistingRelationData(): Promise<Record<string, any[]>> {
    try {
      const businessAreas = await this.organisationProvider.getBusinessAreas(this.organisationId);
      return {
        businessArea: businessAreas.payload || []
      };
    } catch (error) {
      console.error("Error fetching relation data for roles:", error);
      return {
        businessArea: []
      };
    }
  }

  async mapEntities(uploadedEntities: any[], existingEntities: any[]): Promise<FP.Entities.IRole[]> {
    return [];
  }

  validateEntities(mappedEntities: FP.Entities.IRole[]): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  validateMappingResult(result: FP.Entities.IRole[]): ValidationResult {
    return this.validateEntities(result);
  }

  protected getJSXElement(step: StepType, state: any): React.ReactElement {
    const businessAreas = (state.processedEntities && state.processedEntities[BUSINESS_AREA_ENTITY_TYPE]) || [];

    return (
      <RoleValidationView
        uploadedData={state.uploadedData || []}
        businessAreas={businessAreas}
        processedEntities={state.processedEntities || {}}
        onComplete={this.handleComplete}
      />
    );
  }

  private handleComplete = (roles: FP.Entities.IRole[]) => {
    const validationResult = this.validateEntities(roles);

    if (!validationResult.valid) {
      toastService
        .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
        .setContent(<span>{validationResult.errors[0].message}</span>);
      return;
    }

    if (this.onNext) {
      this.onNext({
        type: StepType.entityRelationValidation,
        data: roles
      });
    }
  };
}
